import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import "./index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { CardHeader } from "react-bootstrap";
import CollectItemsStep, { CollectItem, Menu } from "./CollectItemsStep";
import CartPersonDetailsStep from "./CartPersonDetailsStep";
import OrderSummaryStep from "./OrderSummaryStep";
import { useForm } from "react-hook-form";
import CollectedItemsTable from "./CollectItemsStep/CollectedItemsTable";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem } from "../../../redux/menuSlice";
import { useLazyGetMenuListQuery } from "../../../redux/services/MenusService";
import { useLazyGetMenuItemsByRestaurantIdQuery } from "../../../redux/services/menuItemService";
import { useLazyGetMenuListByRestaurantIdQuery } from "../../../redux/services/setMenuService";
import { useLazyFetchCustomMenuListQuery } from "../../../redux/services/customizeMenuService";
import { SetMenu } from "../../../redux/setMenuSlice";
import { CustomMenuItem } from "../../../redux/customizeMenuSlice";
import {
  useCreateOrderMutation,
  useGetOrderPaymentBreakdownMutation,
} from "../../../redux/services/orderService";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useNavigate, useLocation } from "react-router-dom";
import { useCreateAnonymousCustomerMutation } from "../../../redux/services/anonymousUser";
import { toast } from "react-toastify";
import PlaceOrderButton from "../../../components/LongButtonComponent";
import { handaleOrdreTotalPrice } from "../../../redux/orderSlice";
import { formatTimestamp, normalizePhoneNumber, validateSriLankanPhoneNumber } from "../../../utility/Utility";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { useFirebaseCartSummaries } from "../../../hooks/useFirebaseOrders";

interface DeliveryAddress {
  longitude: number;
  latitude: number;
  address: string;
}

interface ScheduledOrderDetails {
  isScheduledOrder: boolean;
  orderTimeSlot: string | null;
}
type CreateOrderFormData = {
  control: any;
  address: String;
  email: any;
  phoneNumber: any;
  cartId: String;
  displayName: String;
  imageUrl: String;
  orderCreatedBy: String;
  orderItems: any;
  paymentMethod: any;
  restaurantId: String;
  customerId: String;
  deliveryTime: Date;
  isTentativeOrder: boolean;
  isScheduledOrder: boolean;
  isCollectCustomItem: boolean;
  isDeliveryOrder: boolean;
  deliveryAddress: DeliveryAddress | null;
  cartPerson: any;
  orderPerson: any;
  orderBy: any;
  date: any;
  orderTimeSlot: any;
  user: any;
  userName: string;
  cart: any;
  organization?: any;
  onlinePaymentReference?: string;
  deliveryInstructions?: string;
  deliveryContactName?: string;
  deliveryContactPhoneNumber?: string;
  orderNote?: string;
  packingNote?: string;
  paymentNote?: string;
  organizationId?: string | null;
  isCorporateOrder?: boolean;
  promotionCode?: any;
  enableManualDeliveryFee?: boolean;
  manualDeliveryFee?: number;
  enableManualDiscount?: boolean;
  manualDiscount?: number;
  manualDiscountType?: { value: string; label: string };
};

const CreateOrder: React.FC = () => {
  const dispatch = useDispatch();

  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();

  const location = useLocation();
  const reorderData = location.state?.reorderData;

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const selectedRestaurantData = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails
  );
  const restaurantState = useSelector((state: any) => state.restaurant);
  // Transform backend delivery time slots to required format
  const deliveryTimeSlots = useMemo(() => {
    // Try to get delivery time slots from selectedRestaurantData
    let backendSlots = selectedRestaurantData?.deliveryTimeSlotsAdmin || restaurantState?.data?.deliveryTimeSlotsAdmin;

    // If not available, try to get from restaurant state as backup
    if (!backendSlots && (restaurantState?.selectedRestaurantDetails?.deliveryTimeSlotsAdmin || restaurantState?.data?.deliveryTimeSlotsAdmin)) {
      backendSlots = restaurantState.selectedRestaurantDetails.deliveryTimeSlotsAdmin || restaurantState.data.deliveryTimeSlotsAdmin;
    }

    if (!backendSlots || !Array.isArray(backendSlots)) {
      console.warn("No delivery time slots found, using default slots");
      // Return default time slots if none are available
      return [
        { value: "12:00-12:30", label: "12:00 PM - 12:30 PM" },
        { value: "12:30-13:00", label: "12:30 PM - 1:00 PM" },
        { value: "13:00-13:30", label: "1:00 PM - 1:30 PM" },
      ];
    }

    return backendSlots.map((slot: string) => {
      // Convert "12:00 PM - 12:30 PM" to "12:00-12:30" format
      const timeRegex = /(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i;
      const match = slot.match(timeRegex);

      if (match) {
        const [, startTime, startPeriod, endTime, endPeriod] = match;

        // Convert to 24-hour format for value
        const convertTo24Hour = (time: string, period: string) => {
          const [hours, minutes] = time.split(':');
          let hour24 = parseInt(hours);

          if (period.toUpperCase() === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
            hour24 = 0;
          }

          return `${hour24.toString().padStart(2, '0')}:${minutes}`;
        };

        const startTime24 = convertTo24Hour(startTime, startPeriod);
        const endTime24 = convertTo24Hour(endTime, endPeriod);

        return {
          value: `${startTime24}-${endTime24}`,
          label: slot
        };
      }

      // Fallback if regex doesn't match
      return {
        value: slot.replace(/\s/g, '').toLowerCase(),
        label: slot
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantData?.deliveryTimeSlotsAdmin, restaurantState?.data?.deliveryTimeSlotsAdmin]);

  const pickupTimeSlots = useMemo(() => {
    let backendSlots = selectedRestaurantData?.pickupTimeSlotsAdmin || restaurantState?.data?.pickupTimeSlotsAdmin;
    console.log("pickupTimeSlotsAdmin from Redux:", backendSlots);

    if (!backendSlots && (restaurantState?.selectedRestaurantDetails?.pickupTimeSlotsAdmin || restaurantState?.data?.pickupTimeSlotsAdmin)) {
      backendSlots = restaurantState.selectedRestaurantDetails.pickupTimeSlotsAdmin || restaurantState.data.pickupTimeSlotsAdmin;
    }

    if (!backendSlots || !Array.isArray(backendSlots)) {
      console.warn("No pickup time slots found, using default slots");
      // Return default time slots if none are available
      return [
        { value: "10:00-10:30", label: "10:00 AM - 10:30 AM" },
        { value: "10:30-11:00", label: "10:30 AM - 11:00 AM" },
        { value: "11:00-11:30", label: "11:00 AM - 11:30 AM" },
        { value: "11:30-12:00", label: "11:30 AM - 12:00 PM" },
        { value: "12:00-12:30", label: "12:00 PM - 12:30 PM" },
        { value: "12:30-13:00", label: "12:30 PM - 1:00 PM" },
        { value: "13:00-13:30", label: "1:00 PM - 1:30 PM" },
        { value: "13:30-14:00", label: "1:30 PM - 2:00 PM" },
      ];
    }

    return backendSlots.map((slot: string) => {
      // Convert "12:00 PM - 12:30 PM" to "12:00-12:30" format
      const timeRegex = /(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/i;
      const match = slot.match(timeRegex);

      if (match) {
        const [, startTime, startPeriod, endTime, endPeriod] = match;

        // Convert to 24-hour format for value
        const convertTo24Hour = (time: string, period: string) => {
          const [hours, minutes] = time.split(':');
          let hour24 = parseInt(hours);

          if (period.toUpperCase() === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
            hour24 = 0;
          }

          return `${hour24.toString().padStart(2, '0')}:${minutes}`;
        };

        const startTime24 = convertTo24Hour(startTime, startPeriod);
        const endTime24 = convertTo24Hour(endTime, endPeriod);

        return {
          value: `${startTime24}-${endTime24}`,
          label: slot
        };
      }

      // Fallback if regex doesn't match
      return {
        value: slot.replace(/\s/g, '').toLowerCase(),
        label: slot
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantData?.pickupTimeSlotsAdmin, restaurantState?.data?.pickupTimeSlotsAdmin]);

  const deliveryTimeSlotsRef = useRef(deliveryTimeSlots);
  const pickupTimeSlotsRef = useRef(pickupTimeSlots);
  useEffect(() => { deliveryTimeSlotsRef.current = deliveryTimeSlots; }, [deliveryTimeSlots]);
  useEffect(() => { pickupTimeSlotsRef.current = pickupTimeSlots; }, [pickupTimeSlots]);

  const [price, setPrice] = useState(0);
  const [orderItems, setOrderItems] = useState<CollectItem[]>([]);
  const [unavailableItemIds, setUnavailableItemIds] = useState<Set<string>>(new Set());
  const [menus, setMenus] = useState<Menu[]>([]);
  const [singleMenuItemsList, setSingleMenuItemsList] = useState<MenuItem[]>([]);
  const [comboItemList, setComboItemList] = useState<SetMenu[]>([]);
  const [customMenuItemList, setCustomMenuItemList] = useState<CustomMenuItem[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<Menu>();
  const [selectedMenuItemList, setSelectedMenuItemList] = useState<MenuItem[]>([]);
  const [sendCustomObj, setSendCustomObj] = useState<MenuItem[]>([]);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [newCustomerId, setNewCustomerId] = useState<any | null>(null);
  const [isNewCustomer, setIsNewCustomer] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<(MenuItem | SetMenu | CustomMenuItem)[]>([]);
  const [isCanCollectItems, setIsCanCollectItems] = useState<boolean>(false);
  const [isCanChangeOrderBy, setIsCanChangeOrderBy] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isTentativeOrder, setIsTentativeOrder] = useState<boolean>(false);
  const [isUserCreationLoading, setIsUserCreationLoading] = useState<boolean>(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [isDoneClicked, setIsDoneClicked] = useState<boolean>(false);
  const [manualDeliveryFeeError, setManualDeliveryFeeError] = useState<string>("");
  const [lastProcessedManualDeliveryFee, setLastProcessedManualDeliveryFee] = useState<number | undefined>(undefined);
  const [manualDiscountError, setManualDiscountError] = useState<string>("");
  const [lastProcessedManualDiscount, setLastProcessedManualDiscount] = useState<number | undefined>(undefined);
  const [clickedButton, setClickedButton] = useState<'addNew' | 'viewDetails' | null>(null);
  const [reorderCheckTrigger, setReorderCheckTrigger] = useState(0);

  const totalPriceRef = useRef<number>(0);
  const isReordering = useRef<boolean>(false);
  const suppressMapAutoOpen = useRef<boolean>(false);
  const isReorderPrefill = useRef<boolean>(false);
  const lastToastedUnavailableRef = useRef<string>("");
  const [reorderInitialAddress, setReorderInitialAddress] = useState<any>(null);
  const [scheduledOrderDetails, setScheduledOrderDetails] = useState<{
    isScheduledOrder: boolean;
    orderTimeSlot: string | null;
  }>({
    isScheduledOrder: false,
    orderTimeSlot: null,
  });
  const [deliveryValidation, setDeliveryValidation] = useState<{
    isValid: boolean;
    isChecking: boolean;
  }>({
    isValid: true,
    isChecking: false,
  });

  const [
    trigger,
    { data: selectedRestaurantDetails, isLoading: isMenuLoading },
  ] = useLazyGetMenuListQuery();
  const [
    menuItemTrigger,
    { data: getMenuItemsList, isLoading: isMenuItemLoading },
  ] = useLazyGetMenuItemsByRestaurantIdQuery();
  const [
    comboItemTrigger,
    { data: getComboItemList, isLoading: isComboLoading },
  ] = useLazyGetMenuListByRestaurantIdQuery();
  const [
    customizeMenuItemTrigger,
    { data: getCustomizeItemList, isLoading: isCustomLoading },
  ] = useLazyFetchCustomMenuListQuery();

  const isInitialLoading =
    isMenuLoading || isMenuItemLoading || isComboLoading || isCustomLoading;

  const [createOrderMutation, { isLoading }] =
    useCreateOrderMutation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userCreationMode, setUserCreationMode] = useState(false);

  const [createAnonymousCustomer, { isLoading: isLoadingCustomerCreate }] =
    useCreateAnonymousCustomerMutation();

  const [
    getOrderPaymentBreakdown,
    {
      isLoading: isPaymentLoading,
    },
  ] = useGetOrderPaymentBreakdownMutation();
  const [paymentDetails, setPaymentDetails] = useState<any | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateOrderFormData>({
    defaultValues: {
      address: "",
      email: "",
      phoneNumber: "",
      cartId: "",
      displayName: "",
      imageUrl: "",
      orderCreatedBy: "",
      orderItems: "",
      paymentMethod: "",
      restaurantId: "",
      customerId: "",
      isCollectCustomItem: false,
      cartPerson: "",
      orderPerson: "",
      orderBy: "",
      date: new Date(),
      orderTimeSlot: deliveryTimeSlots.find((slot: any) => slot.value === "12:30-13:00") || null,
      isTentativeOrder: false,
      isScheduledOrder: false,
      isDeliveryOrder: false,
      deliveryAddress: null,
      onlinePaymentReference: "",
      user: "",
      cart: null,
      deliveryInstructions: "",
      deliveryContactName: "",
      orderNote: "",
      packingNote: "",
      paymentNote: "",
      organizationId: null,
      isCorporateOrder: false,
      promotionCode: null,
      enableManualDeliveryFee: false,
      manualDeliveryFee: undefined,
      enableManualDiscount: false,
      manualDiscount: undefined,
      manualDiscountType: { value: "Fixed", label: "Fixed" },
    },
  });

  const cartPersonWatcher = watch("cartPerson");
  const orderByWatcher = watch("orderBy");
  const emailWatcher = watch("email");
  const phoneNumberWatcher = watch("phoneNumber");
  const customerWatcher = watch("user");
  const displayName = watch("userName");
  const phoneNumber = watch("phoneNumber");
  const paymentMethodWatcher = watch("paymentMethod");
  const isTentativeOrderWatcher = watch("isTentativeOrder");
  const isScheduledOrderWatcher = watch("isScheduledOrder");
  const dateWatcher = watch("date");
  const cartWatcher = watch("cart");
  const isDeliveryOrderWatcher = watch("isDeliveryOrder");
  const deliveryAddressWatcher = watch("deliveryAddress");
  const onlinePaymentReferenceWatcher = watch("onlinePaymentReference");
  const deliveryInstructionsWatcher = watch("deliveryInstructions");
  const orderNoteWatcher = watch("orderNote");
  const packingNoteWatcher = watch("packingNote");
  const paymentNoteWatcher = watch("paymentNote");
  const organizationWatcher = watch("organizationId");
  const deliveryContactNameWatcher = watch("deliveryContactName");
  const promotionCodeWatcher = watch("promotionCode");
  const enableManualDeliveryFeeWatcher = watch("enableManualDeliveryFee");
  const manualDeliveryFeeWatcher = watch("manualDeliveryFee");
  const enableManualDiscountWatcher = watch("enableManualDiscount");
  const manualDiscountWatcher = watch("manualDiscount");
  const manualDiscountTypeWatcher = watch("manualDiscountType");

  // Real-time inventory from Firebase
  // Convert dateWatcher to proper Date object to avoid getTime() errors
  const inventoryDate = dateWatcher ? (dateWatcher instanceof Date ? dateWatcher : new Date(dateWatcher)) : undefined;

  const { data: cartSummariesData, loading: isInventoryLoading } = useFirebaseCartSummaries(
    selectedRestaurantId,
    inventoryDate
  );

  // Debug: Check if organizationId is being set properly
  useEffect(() => {
  }, [organizationWatcher, orderByWatcher]);

  useEffect(() => {
    if (!reorderData) return;
    console.log("REORDER DATA", reorderData);

    // Set guards to prevent side effects during pre-fill
    isReordering.current = true;
    suppressMapAutoOpen.current = true;
    isReorderPrefill.current = true;
    // Fresh reorder — clear any dedupe signature left over from a previous order/session.
    lastToastedUnavailableRef.current = "";

    // Delay to allow CartPersonDetailsStep async selects to mount and initialize
    const timer = setTimeout(() => {
      setValue("isDeliveryOrder", reorderData.isDeliveryOrder ?? false);

      const createdBy = reorderData.orderCreatedBy?.toLowerCase();
      if (createdBy === "customer" || createdBy === "user") {
        setValue("orderBy", { value: "User", label: "Customer" });
        if (reorderData.customer?.id) {
          setValue("user", {
            value: reorderData.customer.id,
            label: reorderData.customer.displayName,
            phoneNumber: reorderData.customer.phoneNumber,
          });
        }
        // For pickup reorders, prefill the cart field
        if (!reorderData.isDeliveryOrder && reorderData.cart?.cartId) {
          setValue("cart", {
            value: reorderData.cart.cartId,
            label: reorderData.cart.displayName,
          });
        }
      } else if (createdBy === "cartperson") {
        setValue("orderBy", { value: "CartPerson", label: "Cart Person" });
        if (reorderData.cart?.cartId) {
          setValue("cartPerson", {
            value: reorderData.cart.cartId,
            label: reorderData.cart.displayName,
          });
        }
      } else if (createdBy === "restaurant") {
          if (reorderData.organization?.organizationId || reorderData.organization?.id) {
            setValue("orderBy", { value: "Organization", label: "Organization" });
            // Pass the full object so ControlledSearch can display the label
            const orgValue = {
              value: reorderData.organization.organizationId || reorderData.organization.id,
              label: reorderData.organization.displayName || reorderData.organization.organizationId || reorderData.organization.id,
              displayName: reorderData.organization.displayName,
              phoneNumber: reorderData.organization.phoneNumber,
            };
            setValue("organization", orgValue);
            setValue("organizationId", orgValue.value);
          } else if (reorderData.customer?.id) {
          setValue("orderBy", { value: "User", label: "Customer" });
          setValue("user", {
            value: reorderData.customer.id,
            label: reorderData.customer.displayName,
            phoneNumber: reorderData.customer.phoneNumber,
          });
        } else if (reorderData.cart?.cartId) {
          setValue("orderBy", { value: "CartPerson", label: "Cart Person" });
          setValue("cartPerson", {
            value: reorderData.cart.cartId,
            label: reorderData.cart.displayName,
          });
        }
        // For pickup reorders from restaurant orders, also prefill the cart
        if (!reorderData.isDeliveryOrder && reorderData.cart?.cartId) {
          setValue("cart", {
            value: reorderData.cart.cartId,
            label: reorderData.cart.displayName,
          });
        }
      }

      if (reorderData.phoneNumber) {
        setValue("phoneNumber", reorderData.phoneNumber);
      }

      // Card payments can't be reused — the customer must pay fresh each time
        const skipPaymentMethods = ["Card", "CardPlusWallet"];
        if (reorderData.paymentMethod && !skipPaymentMethods.includes(reorderData.paymentMethod)) {
          setValue("paymentMethod", {
            value: reorderData.paymentMethod,
            label: reorderData.paymentMethod,
          });
        }

      setValue("orderNote", reorderData.orderNote || "");
      setValue("packingNote", reorderData.packingNote || "");
      setValue("paymentNote", reorderData.paymentNote || "");

      if (reorderData.isDeliveryOrder && reorderData.deliveryDetails) {
        setValue("deliveryInstructions", reorderData.deliveryDetails.deliveryInstructions || "");
        setValue("deliveryContactName", reorderData.deliveryDetails.deliveryContactName || "");
        setValue("deliveryContactPhoneNumber", reorderData.deliveryDetails.deliveryContactPhoneNumber || "");
        if (reorderData.deliveryDetails.address) {
          const prefillAddress = {
            address: reorderData.deliveryDetails.address,
            latitude: reorderData.deliveryDetails.coordinates?.latitude ?? null,
            longitude: reorderData.deliveryDetails.coordinates?.longitude ?? null,
            tag: "Previous Order Address",
          };
          setValue("deliveryAddress", prefillAddress);
          setReorderInitialAddress(prefillAddress);
        }
      }

      // Set items after all form fields are set so the clear-items effect
      // (which watches organizationWatcher/dateWatcher) has already settled
      setTimeout(() => {
        // Pre-fill time slot using refs (avoids stale closure over useMemo value).
        // Match on whichever slot field the previous order actually has, rather than
        // strictly gating on reorderData.isDeliveryOrder — deliveryTimeSlot and
        // pickupTimeSlot are mutually exclusive on a saved order, so this is just as
        // correct when the flag matches, but also recovers the slot in the (otherwise
        // silently-dropped) case where the isDeliveryOrder flag was missing/wrong on
        // the source order while the slot value itself was saved correctly.
        if (reorderData.deliveryTimeSlot) {
          const matchedSlot = deliveryTimeSlotsRef.current.find(
            (slot: any) => slot.value === reorderData.deliveryTimeSlot
          );
          setValue(
            "orderTimeSlot",
            matchedSlot ?? { value: reorderData.deliveryTimeSlot, label: reorderData.deliveryTimeSlot }
          );
        } else if (reorderData.pickupTimeSlot) {
          const matchedSlot = pickupTimeSlotsRef.current.find(
            (slot: any) => slot.value === reorderData.pickupTimeSlot
          );
          setValue(
            "orderTimeSlot",
            matchedSlot ?? { value: reorderData.pickupTimeSlot, label: reorderData.pickupTimeSlot }
          );
        }

        // Pre-fill manual delivery fee — set both atomically so validation never
        // fires with enableManualDeliveryFee=true but manualDeliveryFee=undefined.
        // Trust isManualDeliveryFee as the authoritative boolean; fee of 0 is valid
        // (user explicitly set free delivery via manual override).
        const isManualDeliveryFee = reorderData.isManualDeliveryFee ?? false;
        const previousManualDeliveryFee = isManualDeliveryFee
          ? (reorderData.paymentSummary?.deliveryFee ?? reorderData.manualDeliveryFee ?? 0)
          : null;

        if (isManualDeliveryFee) {
          setValue("enableManualDeliveryFee", true);
          setValue("manualDeliveryFee", previousManualDeliveryFee ?? 0);
        }

          if (reorderData.orderItems?.length > 0) {
            setOrderItems(reorderData.orderItems);
          }
        // Release guards after everything has settled
        setTimeout(() => {
          isReordering.current = false;
          suppressMapAutoOpen.current = false;
          isReorderPrefill.current = false;
          setReorderCheckTrigger(prev => prev + 1);
        }, 300);
      }, 100);

    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time validation for manual delivery fee
  useEffect(() => {
    if (enableManualDeliveryFeeWatcher) {
      if (!manualDeliveryFeeWatcher && manualDeliveryFeeWatcher !== 0) {
        setManualDeliveryFeeError("Please enter a manual delivery fee amount");
      } else if (isNaN(Number(manualDeliveryFeeWatcher)) || Number(manualDeliveryFeeWatcher) < 0) {
        setManualDeliveryFeeError("Please enter a valid delivery fee amount (must be 0 or greater)");
      } else {
        setManualDeliveryFeeError("");
      }
    } else {
      setManualDeliveryFeeError("");
    }
  }, [enableManualDeliveryFeeWatcher, manualDeliveryFeeWatcher]);

  // Real-time validation for manual discount
  useEffect(() => {
    if (enableManualDiscountWatcher) {
      // Set default discount type if not already set
      if (!manualDiscountTypeWatcher) {
        setValue("manualDiscountType", { value: "Fixed", label: "Fixed" });
      }

      if (!manualDiscountWatcher && manualDiscountWatcher !== 0) {
        setManualDiscountError("Please enter a manual discount amount");
      } else if (isNaN(Number(manualDiscountWatcher)) || Number(manualDiscountWatcher) < 0) {
        setManualDiscountError("Please enter a valid discount amount (must be 0 or greater)");
      } else if (manualDiscountTypeWatcher?.value === "Percentage" && Number(manualDiscountWatcher) > 100) {
        setManualDiscountError("Percentage discount cannot exceed 100%");
      } else {
        setManualDiscountError("");
      }
    } else {
      setManualDiscountError("");
    }
  }, [enableManualDiscountWatcher, manualDiscountWatcher, manualDiscountTypeWatcher, setValue]);

  // Track manual delivery fee changes and require Done button to be clicked again
  useEffect(() => {
    if (enableManualDeliveryFeeWatcher) {
      // Only reset Done status if the value actually changed and we had processed a previous value
      if (lastProcessedManualDeliveryFee !== undefined &&
        manualDeliveryFeeWatcher !== lastProcessedManualDeliveryFee &&
        isDoneClicked) {
        setIsDoneClicked(false);
      }
    } else {
      // If checkbox is unchecked and we had previously processed a manual delivery fee, require Done click again
      if (lastProcessedManualDeliveryFee !== undefined && isDoneClicked) {
        setIsDoneClicked(false);
      }
    }
  }, [manualDeliveryFeeWatcher, enableManualDeliveryFeeWatcher, lastProcessedManualDeliveryFee, isDoneClicked]);

  // Track manual discount changes and require Done button to be clicked again
  useEffect(() => {
    if (enableManualDiscountWatcher) {
      // Only reset Done status if the value actually changed and we had processed a previous value
      if (lastProcessedManualDiscount !== undefined &&
        manualDiscountWatcher !== lastProcessedManualDiscount &&
        isDoneClicked) {
        setIsDoneClicked(false);
      }
    } else {
      // If checkbox is unchecked and we had previously processed a manual discount, require Done click again
      if (lastProcessedManualDiscount !== undefined && isDoneClicked) {
        setIsDoneClicked(false);
      }
    }
  }, [manualDiscountWatcher, enableManualDiscountWatcher, lastProcessedManualDiscount, isDoneClicked]);

  // Auto-calculate manual discount from per-item discounts
  useEffect(() => {
    const totalPerItemDiscount = orderItems.reduce((sum, item) => {
      return sum + (item?.perItemManualDiscount || 0) * (item?.quantity || 0);
    }, 0);

    if (totalPerItemDiscount > 0) {
      setValue("enableManualDiscount", true);
      setValue("manualDiscount", totalPerItemDiscount);
      setValue("manualDiscountType", { value: "Fixed", label: "Fixed" });
    } else {
      setValue("enableManualDiscount", false);
      setValue("manualDiscount", undefined);
    }
  }, [orderItems, setValue]);

  // Reset date when scheduled order is unchecked
  useEffect(() => {
      if (isReorderPrefill.current) return;
      if (!isScheduledOrderWatcher) {
        setValue("date", null);
      // Only clear orderTimeSlot if it's not a delivery order
      if (!isDeliveryOrderWatcher) {
        setValue("orderTimeSlot", null);
      }
    }
  }, [isScheduledOrderWatcher, isDeliveryOrderWatcher, setValue]);

  // Set default time slot for delivery orders
  useEffect(() => {
    if (isReorderPrefill.current) return;
    if (isDeliveryOrderWatcher && !isScheduledOrderWatcher) {
      const defaultTimeSlot = deliveryTimeSlots.find((slot: any) => slot.value === "12:30-13:00");
      if (defaultTimeSlot) {
        setValue("orderTimeSlot", defaultTimeSlot);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeliveryOrderWatcher, isScheduledOrderWatcher, setValue]);

  // Set default time slot for pickup orders
  useEffect(() => {
    if (isReorderPrefill.current) return;
    if (!isDeliveryOrderWatcher && !isScheduledOrderWatcher) {
      const defaultTimeSlot = pickupTimeSlots.find((slot: any) => slot.value === "12:30-13:00");
      if (defaultTimeSlot) {
        setValue("orderTimeSlot", defaultTimeSlot);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeliveryOrderWatcher, isScheduledOrderWatcher, setValue]);

  // Clear selected items only when organization changes
  useEffect(() => {
    if (isReordering.current) return;
    if (orderItems.length > 0) {
      setOrderItems([]);
      setPrice(0);
      totalPriceRef.current = 0;
      dispatch(handaleOrdreTotalPrice(0));

      setSingleMenuItemsList((prev) => (prev || []).map((it: any) => ({ ...it, quantity: 0 })));
      setComboItemList((prev) => (prev || []).map((it: any) => ({ ...it, quantity: 0 })));
      setCustomMenuItemList((prev) => (prev || []).map((it: any) => ({ ...it, quantity: 0 })));

      setSelectedMenuItemList([]);
      setFilteredData([]);
      setSendCustomObj([]);
      setPaymentDetails(null);
      setIsDoneClicked(false);
      setUnavailableItemIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationWatcher, dispatch]);

  // Check item availability against the relevant date for ALL order types.
    // Runs when: date changes, scheduled toggle changes, reorder completes,
    // menu data finishes loading, or order items change.
    useEffect(() => {
      if (isReordering.current) return;
      if (orderItems.length === 0) {
        setUnavailableItemIds(new Set());
        lastToastedUnavailableRef.current = "";
        return;
      }

      // For reordered items, availableDays isn't carried on the item itself —
      // it has to be looked up from singleMenuItemsList/comboItemList/customMenuItemList.
      // Those three lists load independently and asynchronously; if any item's source
      // list hasn't arrived yet, skip this pass entirely rather than evaluating a
      // partial/incorrect result (and possibly toasting about it). Each list finishing
      // will change its own .length dependency and re-trigger this effect anyway.
      const itemNeedsLookup = (item: any) => !Array.isArray(item.availableDays);
      const sourceListsStillLoading  =
        orderItems.some((item) => itemNeedsLookup(item)) &&
        (getMenuItemsList === undefined ||
          getComboItemList === undefined ||
          getCustomizeItemList === undefined);

      if (sourceListsStillLoading) {
        return;
      }

      // Determine which date to check availability against:
      //   - Scheduled orders  → user-selected future date
      //   - Delivery orders   → dateWatcher (defaults to today) or today
      //   - Pickup orders     → today
      const checkDate =
        isScheduledOrderWatcher && dateWatcher
          ? dateWatcher instanceof Date
            ? dateWatcher
            : new Date(dateWatcher)
          : new Date();

      const dayIndex = checkDate.getDay(); // 0=Sun … 6=Sat

      const newUnavailable = new Set<string>();
      orderItems.forEach((item: any) => {
        const itemId = item.id || item.menuItemId;
        const sourceItem =
          singleMenuItemsList.find((si: any) => si.id === itemId || si.menuItemId === itemId) ||
          comboItemList.find((ci: any) => ci.id === itemId || ci.menuItemId === itemId) ||
          customMenuItemList.find((cmi: any) => cmi.id === itemId || cmi.menuItemId === itemId);

        // availableDays lives on the source menu item (e.g. [1,2,3,4,5] = Mon–Fri).
        // If not present on the collected item, look it up from the source list.
        const availableDays: number[] = Array.isArray(item.availableDays)
          ? item.availableDays
          : Array.isArray((sourceItem as any)?.availableDays)
          ? (sourceItem as any).availableDays
          : [];

        if (availableDays.length === 0 || !availableDays.includes(dayIndex)) {
          newUnavailable.add(itemId);
        }
      });

      setUnavailableItemIds(newUnavailable);

      // Only toast when the actual set of unavailable items has changed since the
      // last toast — prevents firing the same (or a stale partial) warning multiple
      // times as dependencies settle one after another (e.g. during reorder prefill).
      const signature = Array.from(newUnavailable).sort().join(",");
      if (newUnavailable.size > 0 && signature !== lastToastedUnavailableRef.current) {
        toast.warning(
          `${newUnavailable.size} item(s) in your order are not available on the selected date. Please remove them before placing the order.`,
          { autoClose: 5000 }
        );
        setIsDoneClicked(false);
      }
      lastToastedUnavailableRef.current = signature;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      dateWatcher,
      isScheduledOrderWatcher,
      orderItems.length,          // re-check when items are added/removed (incl. reorder set)
      reorderCheckTrigger,        // explicit trigger after reorder guards release
      singleMenuItemsList.length, // re-check when menu data finishes loading
      comboItemList.length,
      customMenuItemList.length,
    ]);

  useEffect(() => {
    if (selectedRestaurantId) {
      trigger(selectedRestaurantId);
      menuItemTrigger(selectedRestaurantId);
      comboItemTrigger(selectedRestaurantId);
      customizeMenuItemTrigger(selectedRestaurantId);
    }
  }, [
    comboItemTrigger,
    customizeMenuItemTrigger,
    menuItemTrigger,
    selectedRestaurantId,
    trigger,
  ]);

  useEffect(() => {
    if (selectedRestaurantDetails?.MenuList) {
      setMenus(selectedRestaurantDetails?.MenuList);
    }
  }, [selectedRestaurantDetails]);

  useEffect(() => {
    if (getMenuItemsList) {
      const items: MenuItem[] =
        getMenuItemsList?.flatMap((category: any) => category.items) || [];
      // Reset quantity for a fresh start
      setSingleMenuItemsList(items.map((it: MenuItem) => ({ ...it, quantity: 0 })));
    }
  }, [getMenuItemsList]);

  useEffect(() => {
    if (getComboItemList) {
      // Reset quantity for a fresh start
      setComboItemList(
        (getComboItemList?.setMenuList || []).map((it: any) => ({ ...it, quantity: 0 }))
      );
    }
  }, [getComboItemList]);

  useEffect(() => {
    if (getCustomizeItemList) {
      // Reset quantity for a fresh start
      setCustomMenuItemList(
        (getCustomizeItemList?.customMenuList || []).map((it: any) => ({ ...it, quantity: 0 }))
      );
    }
  }, [getCustomizeItemList]);

  const onScheduledOrderChange = (details: ScheduledOrderDetails) => {
    setScheduledOrderDetails(details);
  };

  useEffect(() => {
    const hasCartOrCustomerOrOrganization =
      cartPersonWatcher?.value ||
      customerWatcher?.value ||
      (orderByWatcher?.value === "Organization" && organizationWatcher) ||
      (phoneNumberWatcher && displayName);

    // Check if scheduled order requires date selection
    const isScheduledOrderComplete = !isScheduledOrderWatcher || (isScheduledOrderWatcher && dateWatcher);

    if (orderByWatcher?.value && hasCartOrCustomerOrOrganization && isScheduledOrderComplete) {
      setIsCanCollectItems(true);
    } else {
      setIsCanCollectItems(false);
    }
  }, [
    orderByWatcher?.value,
    cartPersonWatcher?.value,
    customerWatcher?.value,
    emailWatcher,
    phoneNumberWatcher,
    displayName,
    paymentMethodWatcher,
    isScheduledOrderWatcher,
    dateWatcher,
    organizationWatcher,
  ]);

  useEffect(() => {
    if (orderItems.length > 0) {
      setIsCanChangeOrderBy(true);
      // setIsCanChangeOrderBy(false);
    } else {
      setIsCanChangeOrderBy(true);
    }

    const total = orderItems.reduce((sum, item) => {
      let itemPrice = item?.calculatedPrice ?? 0;
      const quantity = item?.quantity ?? 0;

      // For custom menu items, use the pre-calculated total from the modal
      if (item.itemType === "CustomMenu" && item.customItemTotal !== undefined) {
        itemPrice = item.customItemTotal;
      }

      // Don't subtract perItemManualDiscount here - API will handle it
      const itemTotal = itemPrice * quantity;

      return sum + itemTotal;
    }, 0);


    totalPriceRef.current = total;
    dispatch(handaleOrdreTotalPrice(total));
    setPrice(total);
    setIsDoneClicked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems]);

  const fetchPaymentBreakdown = async () => {
    const cartId =
      orderByWatcher?.value === "CartPerson"
        ? cartPersonWatcher?.value
        : cartWatcher?.value;

    if ((cartId && orderItems.length > 0) || (isDeliveryOrderWatcher && orderItems.length > 0) || (organizationWatcher && orderItems.length > 0)) {
      const manualDeliveryFee = enableManualDeliveryFeeWatcher && manualDeliveryFeeWatcher !== undefined && manualDeliveryFeeWatcher !== null
        ? parseFloat(manualDeliveryFeeWatcher.toString())
        : undefined;

      const manualDiscount = enableManualDiscountWatcher && manualDiscountWatcher !== undefined && manualDiscountWatcher !== null
        ? {
          type: manualDiscountTypeWatcher?.value || 'Fixed',
          amount: parseFloat(manualDiscountWatcher.toString())
        }
        : undefined;

      try {
        const response = await getOrderPaymentBreakdown({
          cartId: cartId || "",
          orderItems: orderItems.map((item) => ({
            displayName: item.displayName || "Unknown Item",
            menuItemId: item.id || "",
            quantity: item.quantity || 0,
            unitPrice: item.calculatedPrice || 0,
            itemType: item.itemType || "Unknown",
            portionSize: item.portionLabel || "Regular",
            customItems: item?.itemType === "CustomMenu" && Array.isArray(item.customItems)
              ? item.customItems
              : undefined,
            perItemManualDiscount: item.perItemManualDiscount || undefined,
          })),
          restaurantId: selectedRestaurantId || "",
          totalPrice: totalPriceRef.current || 0,
          isDeliveryOrder: isDeliveryOrderWatcher || false,
          promoCode: enableManualDiscountWatcher ? undefined : (promotionCodeWatcher?.code || undefined),
          manualDeliveryFee,
          manualDiscount,
        }).unwrap();
        setPaymentDetails(response);
        setPrice(response.totalPrice);
        setIsTentativeOrder(isTentativeOrderWatcher);
        setIsDoneClicked(true);
        // Track the manual delivery fee that was processed
        setLastProcessedManualDeliveryFee(manualDeliveryFee);
        // Track the manual discount that was processed
        setLastProcessedManualDiscount(manualDiscount?.amount);
      } catch (error) {
        console.error("Error fetching payment breakdown:", error);
        setPaymentDetails(null);
        setIsDoneClicked(true);
        // Track the manual delivery fee that was attempted to be processed
        setLastProcessedManualDeliveryFee(manualDeliveryFee);
        // Track the manual discount that was attempted to be processed
        setLastProcessedManualDiscount(manualDiscount?.amount);
      }
    } else {
      setPaymentDetails(null);
      setIsDoneClicked(false);
    }
  };

  useEffect(() => {
    fetchPaymentBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderByWatcher?.value,
    cartPersonWatcher?.value,
    cartWatcher?.value,
    selectedRestaurantId,
    getOrderPaymentBreakdown,
    isTentativeOrderWatcher,
    promotionCodeWatcher,
    enableManualDeliveryFeeWatcher,
    manualDeliveryFeeWatcher,
    enableManualDiscountWatcher,
    manualDiscountWatcher,
    manualDiscountTypeWatcher,
  ]);

  // const handleDeliveryValidationChange = (isValid: boolean, isChecking: boolean) => {
  //   setDeliveryValidation({ isValid, isChecking });
  // };

  const resetDeliveryStates = () => {
    setValue("deliveryAddress", null);
    setDeliveryValidation({
      isValid: true,
      isChecking: false,
    });
  };

  const formatOrderDetailsForClipboard = (orderResponse: string) => {
    // Extract data directly from the API response
    const orderData = JSON.parse(orderResponse);

    // Get customer name based on order type
    let customerName = "";
    if (orderData.organization) {
      customerName = orderData.organization.displayName || "N/A";
    } else if (orderData.customer) {
      customerName = orderData.customer.displayName || "N/A";
    } else if (orderData.cart) {
      customerName = orderData.cart.displayName || "N/A";
    }

    // Get contact number
    let contactNumber = "";
    if (orderData.deliveryDetails?.deliveryContactPhoneNumber) {
      contactNumber = orderData.deliveryDetails.deliveryContactPhoneNumber;
    } else if (orderData.organization) {
      contactNumber = orderData.organization.phoneNumber || orderData.phoneNumber || "N/A";
    } else {
      contactNumber = orderData.phoneNumber || "N/A";
    }

    // Get delivery details
    let deliveryOrCart = "";
    let deliveryAddress = "";
    let mapLocation = "";

    if (orderData.isDeliveryOrder) {
      deliveryOrCart = "Delivery";
      deliveryAddress = orderData.deliveryDetails?.address || orderData.address || "N/A";

      // Try to create Google Maps link if coordinates are available
      const latitude = orderData.deliveryLocation?.latitude || orderData.deliveryDetails?.latitude;
      const longitude = orderData.deliveryLocation?.longitude || orderData.deliveryDetails?.longitude;

      if (latitude && longitude) {
        mapLocation = `https://www.google.com/maps?q=${latitude},${longitude}`;
      } else {
        // Fallback to address if no coordinates
        const address = orderData.deliveryDetails?.address || orderData.address || "N/A";
        if (address !== "N/A") {
          mapLocation = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
        } else {
          mapLocation = "N/A";
        }
      }
    } else {
      deliveryOrCart = "Cart pick up";
      deliveryAddress = orderData.cart.displayName || "N/A";
      mapLocation = orderData.cart.displayName || "N/A";
    }

    // Get payment method - check for promo code first
    let paymentMethod = orderData.paymentMethod || "N/A";
    if (orderData.promoCode || orderData.promotionCode) {
      paymentMethod = "Promo";
    }

    // Get special requests
    const specialRequests = [
      orderData.deliveryDetails?.deliveryInstructions,
      orderData.orderNote
    ].filter(Boolean).join(", ") || "";

    // Get packing note
    const packingNote = orderData.packingNote || "";

    // Get payment note
    const paymentNote = orderData.paymentNote || "";

    // Get total bill value
    const totalBill = orderData.paymentSummary?.netAmount?.toFixed(2) || orderData.totalPrice?.toFixed(2) || "0.00";

    // Process order items by food type
    const foodTypes: {
      Meatballs: string[];
      Chicken: string[];
      Fish: string[];
      Egg: string[];
      Vegetable: string[];
    } = {
      Meatballs: [],
      Chicken: [],
      Fish: [],
      Egg: [],
      Vegetable: []
    };

    (orderData.orderItems || []).forEach((item: any) => {
      const itemName = item.displayName || "Unknown Item";
      const quantity = item.quantity || 0;
      // const portionSize = item.portionSize || "Regular";

      // Extract the dish type and name from displayName
      // Example: "Chicken Rice and Curry Gold" -> "Rice & Curry Gold"
      // Example: "Fish Rice & Curry (Gold)" -> "Rice & Curry Gold"
      // Example: "Egg Fried Rice (Bronze)" -> "Fried Rice Bronze"
      // let dishName = itemName;

      // Remove only the parentheses but keep the content inside
      // "(Bronze)" -> "Bronze", "(Gold)" -> "Gold"
      // dishName = dishName.replace(/\(([^)]*)\)/g, '$1').trim();

      // Remove leading food type words and clean up
      // dishName = dishName.replace(/^(Chicken|Fish|Egg|Vegetable|Meatball)\s+/i, '').trim();

      // Replace "and" with "&" for consistency
      // dishName = dishName.replace(/\s+and\s+/gi, ' & ');

      // Create the formatted item string: "Rice & Curry Gold Regular 1"
      const itemString = `${itemName} ${quantity}`;

      // Categorize items based on keywords in the original display name
      const itemLower = itemName.toLowerCase();
      if (itemLower.includes('meat ball') || itemLower.includes('meatball') || itemLower.includes('meatballs') || itemLower.includes('meat balls')) {
        foodTypes.Meatballs.push(itemString);
      } else if (itemLower.includes('chicken')) {
        foodTypes.Chicken.push(itemString);
      } else if (itemLower.includes('fish')) {
        foodTypes.Fish.push(itemString);
      } else if (itemLower.includes('egg')) {
        foodTypes.Egg.push(itemString);
      } else if (itemLower.includes('vegetable') || itemLower.includes('veg')) {
        foodTypes.Vegetable.push(itemString);
      } else {
        // Default to Chicken if can't categorize
        foodTypes.Chicken.push(itemString);
      }
    });

    // Get delivery time information
    let deliveryTime = "";
    if (orderData.deliveryTimeSlot) {
      deliveryTime = orderData.deliveryTimeSlot;
    } else if (orderData.pickupTimeSlot) {
      deliveryTime = orderData.pickupTimeSlot;
    }
    else if (orderData.deliverAt) {
      // Handle Firestore timestamp format
      let deliverAt;
      if (orderData.deliverAt._seconds) {
        deliverAt = formatTimestamp(orderData.deliverAt);
      } else {
        deliverAt = formatTimestamp(orderData.deliverAt);
      }
      deliveryTime = deliverAt.toLocaleString();
    } else {
      deliveryTime = "N/A";
    }

    // Get agent name
    const agentName = orderData.orderTakenBy?.displayName || "Call center";

    // Build packing list with items and custom items
    let packingListText = "Packing List\n";
    (orderData.orderItems || []).forEach((item: any) => {
      const isCustomProduct = item.itemType === "CustomMenu";
      packingListText += `${item.tinyId || ""} ${item.displayName}${isCustomProduct ? " (Custom)" : ""} - ${item.quantity}\n`;
      
      // Add custom items if this is a CustomMenu item
      if (isCustomProduct && item.customItems && item.customItems.length > 0) {
        item.customItems.forEach((customItem: any) => {
          packingListText += `  • ${customItem.displayName || "Item"} - ${customItem.quantity || 1}\n`;
        });
      }
    });

    // Format the clipboard text
    const clipboardText = `Order Number\t:\t${orderData.orderReference || "N/A"}
Name\t:\t${customerName}
Contact Number\t:\t${contactNumber}
Delivery or Pickup Time\t:\t${deliveryTime}
***********************
Delivery or Cart\t:\t${deliveryOrCart}
Delivery Address\t:\t${deliveryAddress}
Map Location\t:\t${mapLocation}
Payment Method\t:\t${paymentMethod}
${paymentNote ? `Payment Note\t:\t${paymentNote}\n` : ''}Special Requests\t:\t${specialRequests}
Total Bill Value\t:\t${totalBill}
***********************
Chicken\t:\t${foodTypes.Chicken.join(', ') || ''}
Fish\t:\t${foodTypes.Fish.join(', ') || ''}
Egg\t:\t${foodTypes.Egg.join(', ') || ''}
Meatballs\t:\t${foodTypes.Meatballs.join(', ') || ''}
Vegetable\t:\t${foodTypes.Vegetable.join(', ') || ''}
***********************
${packingListText}${packingNote ? `Packing Note\t:\t${packingNote}\n` : ''}Agent\t:\t${agentName}`;

    return clipboardText;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Order details copied to clipboard!", { autoClose: 2000 });
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy to clipboard. Please copy manually.", { autoClose: 3000 });
    }
  };

  const orderPlaceSuccessPopup = (response: string, parsedResponse: any) => {
    // Format order details for clipboard using the raw response
    const clipboardText = formatOrderDetailsForClipboard(response);

    return MySwal.fire({
      title: "Order Placed Successfully..!",
      text: `Reference : ${parsedResponse?.orderReference}`,
      icon: "success",
      showCloseButton: true,
      confirmButtonText: "Copy Order Details",
      customClass: {
        confirmButton: "btn btn-success",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Copy to clipboard
        copyToClipboard(clipboardText);
      }
      setResetTrigger(prev => prev + 1);
      resetAllStates();
    });
  };

  const deliveryT = (data: any) => {
    function createDeliveryTimeWithEndTime(
      dateInput: string[] | string | Date | null,
      orderTimeSlot: { value: string }
    ) {
      if (!orderTimeSlot?.value) {
        console.error("Order time slot is missing");
        return null;
      }

      // Handle different date input formats
      let date: Date;
      if (Array.isArray(dateInput)) {
        // If it's an array, take the first element
        date = new Date(dateInput[0]);
      } else if (typeof dateInput === 'string') {
        date = new Date(dateInput);
      } else if (dateInput instanceof Date) {
        date = dateInput;
      } else {
        console.error("Invalid date format:", dateInput);
        return null;
      }

      let startTime, endTime;

      if (orderTimeSlot.value.includes(" - ")) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [startTime, endTime] = orderTimeSlot.value.split(" - ");
      } else if (orderTimeSlot.value.includes("-")) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [start, end] = orderTimeSlot.value.split("-");

        const formatTime = (time24: string) => {
          const [hours, minutes] = time24.split(":").map(Number);
          const period = hours >= 12 ? "PM" : "AM";
          const hours12 = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
          return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
        };

        // startTime = formatTime(start);
        endTime = formatTime(end);
      } else {
        console.error("Invalid time slot format:", orderTimeSlot.value);
        return null;
      }

      function convertTo24HourTime(time: string, date: Date): Date {
        if (!time) {
          console.error("Time is undefined or null");
          return new Date(date);
        }

        const timeParts = time.trim().split(" ");
        if (timeParts.length < 2) {
          console.error("Invalid time format:", time);
          return new Date(date);
        }

        const [timeString, modifier] = timeParts;
        const timeSplit = timeString.split(":");

        if (timeSplit.length < 2) {
          console.error("Invalid time string format:", timeString);
          return new Date(date);
        }

        let [hours, minutes] = timeSplit.map(Number);

        if (isNaN(hours) || isNaN(minutes)) {
          console.error("Invalid hours or minutes:", timeString);
          return new Date(date);
        }

        if (modifier === "PM" && hours !== 12) {
          hours += 12;
        } else if (modifier === "AM" && hours === 12) {
          hours = 0;
        }

        const resultDate = new Date(date);
        resultDate.setHours(hours, minutes, 0, 0);
        return resultDate;
      }

      try {
        const endDateTime = convertTo24HourTime(endTime, date);

        // Format the date in local timezone instead of UTC
        const year = endDateTime.getFullYear();
        const month = String(endDateTime.getMonth() + 1).padStart(2, '0');
        const day = String(endDateTime.getDate()).padStart(2, '0');
        const hours = String(endDateTime.getHours()).padStart(2, '0');
        const minutes = String(endDateTime.getMinutes()).padStart(2, '0');
        const seconds = String(endDateTime.getSeconds()).padStart(2, '0');

        const localTimeString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000`;

        return localTimeString;
      } catch (error) {
        console.error("Error calculating delivery time:", error);
        return null;
      }
    }
    // Handle different order types with time slots
    if (data.orderTimeSlot) {
      if (data.isScheduledOrder) {
        return createDeliveryTimeWithEndTime(data.date, data.orderTimeSlot);
      } else if (data.isDeliveryOrder) {
        return createDeliveryTimeWithEndTime(new Date(), data.orderTimeSlot);
      }
    }
    return null;
  };

  const hasCustomer = useMemo(() => {
    if (orderByWatcher?.value !== "User") return null;
    if (isNewCustomer) {
      return newCustomerId || createdUserId || null;
    }
    return customerWatcher?.value || null;
  }, [orderByWatcher?.value, isNewCustomer, newCustomerId, createdUserId, customerWatcher]);

  const onSubmit = async (data: any, options?: { viewAfter?: boolean }) => {
    const viewAfter = options?.viewAfter ?? true;
    setClickedButton(viewAfter ? 'viewDetails' : 'addNew');

    const validateDeliveryRequirements = () => {
      if (!isDeliveryOrderWatcher) return true;

      // Check delivery address
      if (!deliveryAddressWatcher) {
        toast.error("Please select a delivery address for delivery orders");
        return false;
      }

      // Check if address is verified
      if (deliveryValidation.isChecking) {
        toast.error("Please wait for delivery address verification to complete");
        return false;
      }

      if (deliveryValidation.isValid === false) {
        toast.error("Selected delivery address is outside our delivery zone. Please choose a different address.");
        return false;
      }

      // Get phone number from appropriate source
      let phoneNumber = null;
      if (isNewCustomer) {
        phoneNumber = data.phoneNumber;
      } else if (customerWatcher?.phoneNumber) {
        phoneNumber = customerWatcher.phoneNumber;
      } else {
        phoneNumber = data.phoneNumber;
      }

      // Validate phone number
      if (!phoneNumber) {
        toast.error("Phone number is required for delivery orders");
        return false;
      }

      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      const validation = validateSriLankanPhoneNumber(normalizedPhone);

      if (!validation.isValid) {
        toast.error(validation.message || "Please enter a valid phone number");
        return false;
      }

      return true;
    };

    const validateManualDeliveryFee = () => {
      // Check if manual delivery fee is enabled but no value is provided
      if (enableManualDeliveryFeeWatcher) {
        if (!manualDeliveryFeeWatcher && manualDeliveryFeeWatcher !== 0) {
          toast.error("Please enter a manual delivery fee amount");
          return false;
        }

        // Check if the value is a valid number
        if (isNaN(Number(manualDeliveryFeeWatcher)) || Number(manualDeliveryFeeWatcher) < 0) {
          toast.error("Please enter a valid delivery fee amount (must be 0 or greater)");
          return false;
        }
      }
      return true;
    };

    const validateManualDiscount = () => {
      // Check if manual discount is enabled but no value is provided
      if (enableManualDiscountWatcher) {
        if (!manualDiscountWatcher && manualDiscountWatcher !== 0) {
          toast.error("Please enter a manual discount amount");
          return false;
        }

        // Check if the value is a valid number
        if (isNaN(Number(manualDiscountWatcher)) || Number(manualDiscountWatcher) < 0) {
          toast.error("Please enter a valid discount amount (must be 0 or greater)");
          return false;
        }

        // Check percentage validation
        if (manualDiscountTypeWatcher?.value === "Percentage" && Number(manualDiscountWatcher) > 100) {
          toast.error("Percentage discount cannot exceed 100%");
          return false;
        }
      }
      return true;
    };

    const validateItemAvailability = () => {
      // Final synchronous safety net: re-derive day-availability right now from
      // orderItems, instead of trusting unavailableItemIds state, which can be
      // momentarily stale relative to the latest item/quantity change.
      const checkDate =
        isScheduledOrderWatcher && dateWatcher
          ? (dateWatcher instanceof Date ? dateWatcher : new Date(dateWatcher))
          : new Date();
      const dayIndex = checkDate.getDay();

      const stillUnavailable = orderItems.filter((item: any) => {
        const itemId = item.id || item.menuItemId;
        const sourceItem =
          singleMenuItemsList.find((si: any) => si.id === itemId || si.menuItemId === itemId) ||
          comboItemList.find((ci: any) => ci.id === itemId || ci.menuItemId === itemId) ||
          customMenuItemList.find((cmi: any) => cmi.id === itemId || cmi.menuItemId === itemId);
        const availableDays: number[] = Array.isArray(item.availableDays)
          ? item.availableDays
          : Array.isArray((sourceItem as any)?.availableDays)
          ? (sourceItem as any).availableDays
          : [];
        return availableDays.length > 0 && !availableDays.includes(dayIndex);
      });

      if (stillUnavailable.length > 0) {
        toast.warning(
          `${stillUnavailable.length} item(s) in your order are not available on the selected date. Please remove them before placing the order.`,
          { autoClose: 5000 }
        );
        setUnavailableItemIds(new Set(stillUnavailable.map((item: any) => item.id || item.menuItemId)));
        return false;
      }

      return true;
    };

    if (!validateItemAvailability()) {
      return;
    }

    if (!validateDeliveryRequirements()) {
      return;
    }

    if (!validateManualDeliveryFee()) {
      return;
    }

    if (!validateManualDiscount()) {
      return;
    }

    const getPhoneNumber = () => {
      let phoneNumber = null;
      if (isNewCustomer) {
        phoneNumber = data.phoneNumber;
      } else if (customerWatcher?.phoneNumber) {
        phoneNumber = customerWatcher.phoneNumber;
      } else {
        phoneNumber = data.phoneNumber;
      }

      return phoneNumber ? normalizePhoneNumber(phoneNumber) : null;
    };

    const sendProductList = Array.isArray(orderItems)
      ? orderItems.map((item: any) => {
        // For CustomMenu items, include the saved custom items under customItems
        let customItems = null;
        if (item?.itemType === "CustomMenu") {
          customItems = Array.isArray(item.customItems)
            ? item.customItems.map((customItem: any) => ({
              menuItemId: customItem.itemId,
              displayName: customItem.displayName,
              unitPrice: customItem.unitPrice,
              productId: customItem.productId,
              quantity: customItem.quantity,
              isAddOn: customItem.isAddOn || false,
              ...(customItem.tinyId && { tinyId: customItem.tinyId }),
              ...(customItem.skuId && { skuId: customItem.skuId }),
            }))
            : null;
        }
        return {
          // menuId: item.menuId || null,
          tinyId: item.tinyId || undefined,
          productId: item.productId || item.id,
          displayName: item.displayName,
          menuItemId: item.id,
          quantity: item.quantity,
          unitPrice: item.calculatedPrice,
          unitTotalPrice: item.itemType === "CustomMenu" ? item.customItemTotal : undefined,
          itemType: item.itemType === "Single" ? "MenuItem" : item.itemType,
          orderCreatedBy: "Restaurant",
          customItems,
          portionSize: item.portionLabel,
          perItemManualDiscount: item.perItemManualDiscount || 0,
          skuId: item.itemType === "Single" || item.itemType === "MenuItem" ? item.skuId : undefined,
        };
      })
      : [];

    // For delivery orders, cart is not required
    const hasCart = orderByWatcher.value === "CartPerson"
      ? cartPersonWatcher?.value
      : !isDeliveryOrderWatcher // Only require cart for pickup orders
        ? cartWatcher?.value
        : null;

    const deliveryPhoneNumber = getPhoneNumber();

    // Validate cart for pickup orders when order by User
    if (orderByWatcher?.value === "User" && !isDeliveryOrderWatcher && !hasCart) {
      toast.error("Please select a cart for pickup orders");
      return;
    }

    const isDirectRestaurantOrder = !hasCart;

    const deliveryLocation = isDeliveryOrderWatcher && deliveryAddressWatcher ? {
      latitude: deliveryAddressWatcher?.latitude || null,
      longitude: deliveryAddressWatcher?.longitude || null,
      address: deliveryAddressWatcher?.address || null,
    } : null;

    const obj = {
      address: isDeliveryOrderWatcher ? deliveryAddressWatcher?.address : selectedRestaurantData?.address,
      cartId: hasCart,
      displayName: selectedRestaurantData?.displayName,
      imageUrl: selectedRestaurantData.imageUrl,
      orderCreatedBy: "Restaurant",
      paymentMethod: paymentMethodWatcher?.value,
      restaurantId: selectedRestaurantId,
      customerId: hasCustomer,
      ...(isScheduledOrderWatcher || isDeliveryOrderWatcher ? { deliveryTime: deliveryT(data) } : {}),
      isTentativeOrder: isTentativeOrderWatcher,
      isScheduledOrder: isScheduledOrderWatcher,
      isDeliveryOrder: isDeliveryOrderWatcher,
      channel: isDeliveryOrderWatcher ? "WebDelivery" : "WebPickup",
      deliveryLocation: deliveryLocation,
      phoneNumber: deliveryPhoneNumber,
      deliveryContactPhoneNumber: watch("deliveryContactPhoneNumber") || undefined,
      onlinePaymentReference: onlinePaymentReferenceWatcher,
      totalPrice: price,
      orderItems: sendProductList,
      isDirectRestaurantOrder: isDirectRestaurantOrder,
      orderStatus: isTentativeOrderWatcher ? "Tentative" : "ReadyAtKitchen",
      orderSource: "CallCenter" as const,
      deliveryInstructions: deliveryInstructionsWatcher,
      deliveryContactName: deliveryContactNameWatcher,
      orderNote: orderNoteWatcher,
      packingNote: packingNoteWatcher,
      paymentNote: paymentNoteWatcher,
      organizationId: organizationWatcher || null,
      isCorporateOrder: orderByWatcher?.value === "Organization" ? true : false,
      ...(!enableManualDiscountWatcher && promotionCodeWatcher?.code ? { promoCode: promotionCodeWatcher.code } : {}),
      deliveryTimeSlot: isDeliveryOrderWatcher && !isScheduledOrderWatcher && data.orderTimeSlot ? data.orderTimeSlot.value : null,
      pickupTimeSlot: !isDeliveryOrderWatcher && !isScheduledOrderWatcher && data.orderTimeSlot ? data.orderTimeSlot.value : null,
      ...(enableManualDeliveryFeeWatcher && manualDeliveryFeeWatcher !== undefined && manualDeliveryFeeWatcher !== null ?
        { manualDeliveryFee: parseFloat(manualDeliveryFeeWatcher.toString()) } : {}),
      ...(enableManualDiscountWatcher && manualDiscountWatcher !== undefined && manualDiscountWatcher !== null ?
        {
          manualDiscount: {
            type: manualDiscountTypeWatcher?.value || 'Fixed',
            amount: parseFloat(manualDiscountWatcher.toString())
          }
        } : {}),
    };

    try {
      const response = await createOrderMutation(obj).unwrap();
      const parsedResponse = JSON.parse(response);
      // Try to extract an order id from common response shapes
      const orderId = parsedResponse?.id || parsedResponse?.orderId || parsedResponse?.order?.id || parsedResponse?.data?.order || parsedResponse?.data?.customer || parsedResponse?.order?.orderId || null;
      // If caller requested to view details after placing the order,
      // reset states and navigate to the order view. Otherwise,
      // show the success popup that allows copying order details and
      // resets state when dismissed.
      if (viewAfter) {
        // Reset local states then navigate depending on delivery vs pickup
        resetAllStates();
        if (obj.isDeliveryOrder) {
          navigate("/orders/delivery-order/view", { state: { orderId } });
        } else {
          navigate("/orders/pickup-orders/view", { state: { orderId } });
        }
      } else {
        // Show the success popup (which handles copy and resets states)
        orderPlaceSuccessPopup(response, parsedResponse);
      }
    } catch (error: any) {
      console.error("Order creation failed:", error);

      // Handle specific error cases
      if (error?.data?.message?.includes("phone")) {
        toast.error("Invalid phone number format. Please check and try again.");
      } else if (error?.data?.message?.includes("delivery")) {
        toast.error("Delivery validation failed. Please check your delivery address.");
      } else {
        toast.error(`Something went wrong. Please try again.`);
      }
    } finally {
      setClickedButton(null);
    }
  };

  const onSubmitNewUserCreation = async (data: any, userId: any, options?: { viewAfter?: boolean }) => {
    const viewAfter = options?.viewAfter ?? true;
    setClickedButton(viewAfter ? 'viewDetails' : 'addNew');
    const validateDeliveryRequirements = () => {
      if (!isDeliveryOrderWatcher) return true;

      // Check delivery address
      if (!deliveryAddressWatcher) {
        toast.error("Please select a delivery address for delivery orders");
        return false;
      }

      // Check if address is verified
      if (deliveryValidation.isChecking) {
        toast.error("Please wait for delivery address verification to complete");
        return false;
      }

      if (deliveryValidation.isValid === false) {
        toast.error("Selected delivery address is outside our delivery zone. Please choose a different address.");
        return false;
      }

      // Get phone number from appropriate source
      let phoneNumber = null;
      if (isNewCustomer) {
        phoneNumber = data.phoneNumber;
      } else if (customerWatcher?.phoneNumber) {
        phoneNumber = customerWatcher.phoneNumber;
      } else {
        phoneNumber = data.phoneNumber;
      }

      // Validate phone number
      if (!phoneNumber) {
        toast.error("Phone number is required for delivery orders");
        return false;
      }

      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      const validation = validateSriLankanPhoneNumber(normalizedPhone);

      if (!validation.isValid) {
        toast.error(validation.message || "Please enter a valid phone number");
        return false;
      }

      return true;
    };

    // Run delivery validation
    if (!validateDeliveryRequirements()) {
      return; // Stop submission if validation fails
    }

    // 🔥 Get normalized phone number for the order
    const getPhoneNumber = () => {
      // if (!isDeliveryOrderWatcher) return null;

      let phoneNumber = null;
      if (isNewCustomer) {
        phoneNumber = data.phoneNumber;
      } else if (customerWatcher?.phoneNumber) {
        phoneNumber = customerWatcher.phoneNumber;
      } else {
        phoneNumber = data.phoneNumber;
      }

      return phoneNumber ? normalizePhoneNumber(phoneNumber) : null;
    };

    // const sendProductList = Array.isArray(orderItems)
    //   ? orderItems.map((item: any) => ({
    //     displayName: item.displayName,
    //     menuItemId: item.id,
    //     quantity: item.quantity,
    //     unitPrice: item.calculatedPrice,
    //     itemType: item.itemType === "Single" ? "MenuItem" : item.itemType,
    //     orderCreatedBy: "Restaurant",
    //     customItems: item?.itemType === "CustomMenu" ? item?.customItems : null,
    //     portionSize: item.portionLabel,
    //   }))
    //   : [];

    const sendProductList = Array.isArray(orderItems)
      ? orderItems.map((item: any) => {
        // For CustomMenu items, include the saved custom items under customItems
        let customItems = null;
        if (item?.itemType === "CustomMenu") {
          customItems = Array.isArray(item.customItems)
            ? item.customItems.map((customItem: any) => ({
              menuItemId: customItem.itemId,
              displayName: customItem.displayName,
              unitPrice: customItem.unitPrice,
              productId: customItem.productId,
              quantity: customItem.quantity,
              isAddOn: customItem.isAddOn || false,
              ...(customItem.tinyId && { tinyId: customItem.tinyId }),
              ...(customItem.skuId && { skuId: customItem.skuId }),
            }))
            : null;
        }
        return {
          // menuId: item.menuId || null,
          tinyId: item.tinyId || undefined,
          displayName: item.displayName,
          menuItemId: item.id,
          quantity: item.quantity,
          unitPrice: item.calculatedPrice,
          unitTotalPrice: item.itemType === "CustomMenu" ? item.customItemTotal : undefined,
          itemType: item.itemType === "Single" ? "MenuItem" : item.itemType,
          orderCreatedBy: "Restaurant",
          customItems,
          portionSize: item.portionLabel,
          perItemManualDiscount: item.perItemManualDiscount || 0,
          skuId: item.itemType === "Single" || item.itemType === "MenuItem" ? item.skuId : undefined,
        };
      })
      : [];
    // For delivery orders, cart is not required
    const hasCart = orderByWatcher.value === "CartPerson"
      ? cartPersonWatcher?.value
      : !isDeliveryOrderWatcher // Only require cart for pickup orders
        ? cartWatcher?.value
        : null;

    const deliveryPhoneNumber = getPhoneNumber();

    // Validate cart for pickup orders when order by User
    if (orderByWatcher?.value === "User" && !isDeliveryOrderWatcher && !hasCart) {
      toast.error("Please select a cart for pickup orders");
      return;
    }

    const isDirectRestaurantOrder = !hasCart;

    // Build delivery location object if it's a delivery order
    const deliveryLocation = isDeliveryOrderWatcher && deliveryAddressWatcher ? {
      latitude: deliveryAddressWatcher?.latitude || null,
      longitude: deliveryAddressWatcher?.longitude || null,
      address: deliveryAddressWatcher?.address || null,
    } : null;

    const obj = {
      address: isDeliveryOrderWatcher ? deliveryAddressWatcher?.address : selectedRestaurantData?.address,
      cartId: hasCart,
      displayName: selectedRestaurantData?.displayName,
      imageUrl: selectedRestaurantData.imageUrl,
      orderCreatedBy: "Restaurant",
      paymentMethod: paymentMethodWatcher?.value,
      restaurantId: selectedRestaurantId,
      customerId: userId,
      ...(isScheduledOrderWatcher || isDeliveryOrderWatcher ? { deliveryTime: deliveryT(data) } : {}),
      isTentativeOrder: isTentativeOrderWatcher,
      isScheduledOrder: isScheduledOrderWatcher,
      isDeliveryOrder: isDeliveryOrderWatcher,
      channel: isDeliveryOrderWatcher ? "WebDelivery" : "WebPickup",
      onlinePaymentReference: onlinePaymentReferenceWatcher,
      deliveryLocation: deliveryLocation,
      phoneNumber: deliveryPhoneNumber,
      deliveryContactPhoneNumber: watch("deliveryContactPhoneNumber") || undefined,
      totalPrice: price,
      orderItems: sendProductList,
      isDirectRestaurantOrder: isDirectRestaurantOrder,
      orderStatus: isTentativeOrderWatcher ? "Tentative" : "ReadyAtKitchen",
      orderSource: "CallCenter" as const,
      deliveryInstructions: deliveryInstructionsWatcher,
      deliveryContactName: deliveryContactNameWatcher,
      orderNote: orderNoteWatcher,
      packingNote: packingNoteWatcher,
      paymentNote: paymentNoteWatcher,
      organizationId: organizationWatcher || null,
      isCorporateOrder: orderByWatcher?.value === "Organization" ? true : false,
      ...(!enableManualDiscountWatcher && promotionCodeWatcher?.code ? { promoCode: promotionCodeWatcher.code } : {}),
      deliveryTimeSlot: isDeliveryOrderWatcher && !isScheduledOrderWatcher && data.orderTimeSlot ? data.orderTimeSlot.value : null,
      pickupTimeSlot: !isDeliveryOrderWatcher && !isScheduledOrderWatcher && data.orderTimeSlot ? data.orderTimeSlot.value : null,
      ...(enableManualDeliveryFeeWatcher && manualDeliveryFeeWatcher !== undefined && manualDeliveryFeeWatcher !== null ?
        { manualDeliveryFee: parseFloat(manualDeliveryFeeWatcher.toString()) } : {}),
      ...(enableManualDiscountWatcher && manualDiscountWatcher !== undefined && manualDiscountWatcher !== null ?
        {
          manualDiscount: {
            type: manualDiscountTypeWatcher?.value || 'Fixed',
            amount: parseFloat(manualDiscountWatcher.toString())
          }
        } : {}),
    };

    try {
      const response = await createOrderMutation(obj).unwrap();
      const parsedResponse = JSON.parse(response);
      const orderId = parsedResponse?.id || parsedResponse?.orderId || parsedResponse?.order?.id || parsedResponse?.data?.order || parsedResponse?.data?.customer || parsedResponse?.order?.orderId || null;
      if (viewAfter) {
        // Reset local states then navigate
        resetAllStates();
        if (obj.isDeliveryOrder) {
          navigate("/orders/delivery-order/view", { state: { orderId } });
        } else {
          navigate("/orders/pickup-orders/view", { state: { orderId } });
        }
      } else {
        // Show popup and allow copying; popup will call resetAllStates()
        orderPlaceSuccessPopup(response, parsedResponse);
      }
    } catch (error: any) {
      console.error("Order creation failed:", error);

      // Handle specific error cases
      if (error?.data?.message?.includes("phone")) {
        toast.error("Invalid phone number format. Please check and try again.");
      } else if (error?.data?.message?.includes("delivery")) {
        toast.error("Delivery validation failed. Please check your delivery address.");
      } else {
        toast.error(`Something went wrong. Please try again.`);
      }
    } finally {
      setClickedButton(null);
    }
  };

  const handleUserCreation = async (viewAfter: boolean = true) => {
    setIsUserCreationLoading(true);
    setClickedButton(viewAfter ? 'viewDetails' : 'addNew');
    const email = watch("email");

    try {
      const response = await createAnonymousCustomer({
        displayName,
        email,
        phoneNumber,
      });

      if (response && "data" in response) {
        const userId = response?.data?.customer ?? null;
        setCreatedUserId(userId);
        setNewCustomerId(userId);
        setIsNewCustomer(true);
        toast.success("User created successfully!", { autoClose: 2000 });
        setUserCreationMode(false);
        MySwal.fire({
          title: "User Created Successfully!",
          text: `User Id: ${response?.data?.customer}`,
          icon: "success",
          confirmButtonText: "Ok",
        }).then(() => {
          const formData = {
            ...watch(),
            customerId: userId
          };
          // onSubmit(formData);
          onSubmitNewUserCreation(formData, userId, { viewAfter });
        });
        setUserCreationMode(false);
        setIsUserCreationLoading(false);
      } else if (response && "error" in response) {
        console.error("Error creating user:", response.error);
        // Check if error is FetchBaseQueryError and has status
        if (
          response?.error &&
          typeof response.error === "object" &&
          "status" in response.error &&
          (response.error as { status?: number }).status === 409
        ) {
          toast.error("Customer Email already exists!", { autoClose: 2000 });
        } else {
          toast.error("Failed to create user!", { autoClose: 2000 });
        }
      }
    } catch (error) {
      toast.error("Failed to create user!");
    } finally {
      setIsUserCreationLoading(false);
      setClickedButton(null);
    }
  };

  const resetAllStates = () => {
    // Reset form
    reset({
      address: "",
      email: "",
      phoneNumber: "",
      cartId: "",
      displayName: "",
      imageUrl: "",
      orderCreatedBy: "",
      orderItems: "",
      paymentMethod: "",
      restaurantId: "",
      customerId: "",
      isCollectCustomItem: false,
      cartPerson: "",
      orderPerson: "",
      orderBy: "",
      date: new Date(),
      orderTimeSlot: deliveryTimeSlots.find((slot: any) => slot.value === "12:30-13:00") || null,
      isTentativeOrder: false,
      isScheduledOrder: false,
      isDeliveryOrder: true,
      deliveryAddress: null,
      onlinePaymentReference: "",
      deliveryInstructions: "",
      deliveryContactName: "",
      orderNote: "",
      user: "",
      userName: "",
      cart: null,
      organizationId: null,
      isCorporateOrder: false,
      promotionCode: null,
      enableManualDeliveryFee: false,
      manualDeliveryFee: undefined,
      enableManualDiscount: false,
      manualDiscount: undefined,
    });

    // Reset all state variables
    setPrice(0);
    setOrderItems([]);
    setSelectedMenu(undefined);
    setSelectedMenuItemList([]);
    // Clear and zero quantities in item lists
    setSingleMenuItemsList((prev) => (prev || []).map((it: any) => ({ ...it, quantity: 0 })));
    setComboItemList((prev) => (prev || []).map((it: any) => ({ ...it, quantity: 0 })));
    setCustomMenuItemList((prev) => (prev || []).map((it: any) => ({ ...it, quantity: 0 })));
    setSendCustomObj([]);
    setCreatedUserId(null);
    setNewCustomerId(null);
    setIsNewCustomer(false);
    setIsTentativeOrder(false);
    setFilteredData([]);
    setIsCanCollectItems(false);
    setIsCanChangeOrderBy(true);
    setUserCreationMode(false);
    setPaymentDetails(null);
    setManualDeliveryFeeError("");
    setLastProcessedManualDeliveryFee(undefined);
    setManualDiscountError("");
    setLastProcessedManualDiscount(undefined);
    setIsDoneClicked(false);
    setUnavailableItemIds(new Set());

    // Reset scheduled order details
    setScheduledOrderDetails({
      isScheduledOrder: false,
      orderTimeSlot: null,
    });

    setDeliveryValidation({
      isValid: true,
      isChecking: false,
    });

    // Reset total price ref
    totalPriceRef.current = 0;

    // Dispatch reset action for Redux store if needed
    dispatch(handaleOrdreTotalPrice(0));

    // Show success message
    toast.success("Order placed successfully!", {
      autoClose: 3000
    });
  };

  useEffect(() => {
    setIsTentativeOrder(isTentativeOrderWatcher);
  }, [isTentativeOrderWatcher]);

  useEffect(() => {
    if (orderItems.length > 0 && isTentativeOrderWatcher !== undefined) {
      const updatedOrderItems = orderItems.map((item: any) => ({
        ...item,
        isTentativeOrder: isTentativeOrderWatcher,
      }));
      setOrderItems(updatedOrderItems);
      setIsTentativeOrder(isTentativeOrderWatcher);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTentativeOrderWatcher]);

  //setIsTentativeOrder(isTentativeOrderWatcher);
  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Row className="mt-0">
        <Col lg={12}>
          <Card className="card">
            <CardHeader className="card-header ">
              <h5 className="header-text">
                {orderByWatcher.value === "CartPerson"
                  ? "Cart Person Details"
                  : orderByWatcher.value === "Organization"
                    ? "Organization Details"
                    : "Customer Details"}
              </h5>
              <hr />
            </CardHeader>
            <CardBody>
              <CartPersonDetailsStep
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                scheduledOrderWatcher={isScheduledOrderWatcher}
                setUserCreationMode={setUserCreationMode}
                orderByWatcher={orderByWatcher}
                cartPersonWatcher={cartPersonWatcher}
                paymentMethodWatcher={paymentMethodWatcher}
                customerWatcher={customerWatcher}
                setIsNewCustomer={setIsNewCustomer}
                isNewCustomer={isNewCustomer}
                isCanChangeOrderBy={isCanChangeOrderBy}
                onScheduledOrderChange={onScheduledOrderChange}
                menus={menus}
                isTentativeOrder={isTentativeOrderWatcher}
                onResetDeliveryStates={resetDeliveryStates}
                resetTrigger={resetTrigger}
                onlinePaymentReferenceWatcher={onlinePaymentReferenceWatcher}
                deliveryInstructionsWatcher={deliveryInstructionsWatcher}
                deliveryContactNameWatcher={deliveryContactNameWatcher}
                orderNoteWatcher={orderNoteWatcher}
                packingNoteWatcher={packingNoteWatcher}
                paymentNoteWatcher={paymentNoteWatcher}
                organizationWatcher={organizationWatcher}
                promotionCodeWatcher={promotionCodeWatcher}
                enableManualDeliveryFeeWatcher={enableManualDeliveryFeeWatcher}
                manualDeliveryFeeWatcher={manualDeliveryFeeWatcher}
                manualDeliveryFeeError={manualDeliveryFeeError}
                suppressMapAutoOpen={suppressMapAutoOpen}
                reorderInitialAddress={reorderInitialAddress}
                isReorderPrefill={isReorderPrefill}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <br></br>
      <Card className="card">
        <CardHeader className="card-header">
          <Row>
            <Col>
              <h5 className="header-text mt-2">Create Order</h5>
            </Col>
          </Row>
          <hr />
        </CardHeader>
        <CardBody className="card-body">
          <CollectItemsStep
            watch={watch}
            setOrderItems={setOrderItems}
            orderItems={orderItems}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            setSelectedMenuItemList={setSelectedMenuItemList}
            menus={menus}
            selectedMenuItemList={
              filteredData.length > 0 ? filteredData : selectedMenuItemList
            }
            setFilteredData={filteredData}
            singleMenuItemsList={singleMenuItemsList}
            comboItemList={comboItemList}
            customMenuItemList={customMenuItemList}
            setSingleMenuItemsList={setSingleMenuItemsList}
            setComboItemList={setComboItemList}
            setCustomMenuItemList={setCustomMenuItemList}
            control={control}
            sendCustomObj={sendCustomObj}
            setSendCustomObj={setSendCustomObj}
            orderByWatcher={orderByWatcher}
            isCanCollectItems={isCanCollectItems}
            scheduledOrderDetails={scheduledOrderDetails}
            isInitialLoading={isInitialLoading}
            isTentativeOrder={isTentativeOrderWatcher}
            onDoneClick={fetchPaymentBreakdown}
            manualDeliveryFeeError={manualDeliveryFeeError}
            manualDiscountError={manualDiscountError}
            cartSummariesData={cartSummariesData}
            isInventoryLoading={isInventoryLoading}
            isDeliveryOrder={isDeliveryOrderWatcher}
            cartId={isDeliveryOrderWatcher ? (selectedRestaurantData?.deliveryCartId || "Tia8TM6vj1ZktAu2CqWL") : (cartPersonWatcher?.value || cartWatcher?.value)}
            deliveryDate={isScheduledOrderWatcher || isDeliveryOrderWatcher ? (dateWatcher || new Date()) : undefined}
            organizationId={organizationWatcher}
          />
        </CardBody>
      </Card>
      {unavailableItemIds.size > 0 && (
          <div className="alert alert-warning mt-2 mb-0" role="alert">
            <strong>⚠ Date conflict:</strong>{" "}
            {orderItems
              .filter((item: any) => unavailableItemIds.has(item.id || item.menuItemId))
              .map((item: any) => item.displayName)
              .join(", ")}{" "}
            {unavailableItemIds.size === 1 ? "is" : "are"} not available on the
            selected date. Please remove{" "}
            {unavailableItemIds.size === 1 ? "it" : "them"} from your order to
            continue.
          </div>
        )}
        <CollectedItemsTable
            orderItems={orderItems}
            unavailableItemIds={unavailableItemIds}
            setOrderItems={(items: any) => {
              setOrderItems(items);
              // Re-check unavailability after item add/remove/quantity change.
              // Use the same date resolution as the main availability effect:
              //   - Scheduled orders → user-selected future date
              //   - Everything else  → today
              // Previously this branch only ran the check for scheduled orders and
              // unconditionally cleared unavailableItemIds otherwise, which let
              // genuinely day-unavailable items silently slip through on immediate
              // (non-scheduled) orders whenever the quantity was touched.
              const checkDate =
                isScheduledOrderWatcher && dateWatcher
                  ? (dateWatcher instanceof Date ? dateWatcher : new Date(dateWatcher))
                  : new Date();
              const dayIndex = checkDate.getDay();
              const newUnavailable = new Set<string>();
              items.forEach((item: any) => {
                const itemId = item.id || item.menuItemId;
                const sourceItem =
                  singleMenuItemsList.find((si: any) => si.id === itemId || si.menuItemId === itemId) ||
                  comboItemList.find((ci: any) => ci.id === itemId || ci.menuItemId === itemId) ||
                  customMenuItemList.find((cmi: any) => cmi.id === itemId || cmi.menuItemId === itemId);
                const availableDays: number[] = Array.isArray(item.availableDays)
                  ? item.availableDays
                  : Array.isArray((sourceItem as any)?.availableDays)
                  ? (sourceItem as any).availableDays
                  : [];
                // No configured available days at all = never available, not "always available".
                if (availableDays.length === 0 || !availableDays.includes(dayIndex)) {
                  newUnavailable.add(itemId);
                }
              });
              setUnavailableItemIds(newUnavailable);
            }}
          />
      <Row>
        <Col lg={12}>
          <OrderSummaryStep
            orderItems={orderItems}
            setOrderItems={setOrderItems}
            cartPersonWatcher={cartPersonWatcher}
            orderByWatcher={orderByWatcher}
            emailWatcher={emailWatcher}
            isLoading={isLoading}
            phoneNumberWatcher={phoneNumberWatcher}
            setPrice={setPrice}
            price={paymentDetails?.netAmount || price}
            paymentDetails={paymentDetails}
            isTentativeOrder={isTentativeOrderWatcher}
            isDeliveryOrder={isDeliveryOrderWatcher}
            manualDeliveryFee={manualDeliveryFeeWatcher}
          />
          <div className="d-flex flex-column align-items-end mb-3">
            {/* Helper text for disabled button */}
            {(manualDeliveryFeeError && enableManualDeliveryFeeWatcher) && (
              <div className="text-danger small mb-2" style={{ textAlign: 'right' }}>
                <i className="fas fa-exclamation-triangle"></i> {manualDeliveryFeeError}
              </div>
            )}
            {(manualDiscountError && enableManualDiscountWatcher) && (
              <div className="text-danger small mb-2" style={{ textAlign: 'right' }}>
                <i className="fas fa-exclamation-triangle"></i> {manualDiscountError}
              </div>
            )}
            {unavailableItemIds.size > 0 && (
              <div className="text-danger small mb-2" style={{ textAlign: 'right' }}>
                <i className="fas fa-exclamation-triangle"></i>{" "}
                {unavailableItemIds.size} item(s) are not available on the
                selected date. Remove them to place the order.
              </div>
            )}

            <div className="d-flex gap-2">
              <PlaceOrderButton
                title={
                  deliveryValidation.isChecking
                    ? "Validating delivery location..."
                    : isPaymentLoading
                      ? "Calculating the final price"
                      : "Place order & Add New Order"
                }
                // require Done click (isDoneClicked) when items exist
                isDisabled={isLoading || orderItems.length === 0 || !isDoneClicked || isPaymentLoading || deliveryValidation.isChecking ||
                  !deliveryValidation.isValid || isUserCreationLoading ||
                  (enableManualDeliveryFeeWatcher && manualDeliveryFeeError !== "") ||
                  (enableManualDiscountWatcher && manualDiscountError !== "") ||
                  unavailableItemIds.size > 0 ||
                  clickedButton === 'viewDetails'}
                onClick={
                  isNewCustomer ? () => handleUserCreation(false) : handleSubmit((data) => onSubmit(data, { viewAfter: false }))
                }
                icon={
                  (isLoading || isLoadingCustomerCreate || isPaymentLoading) && clickedButton === 'addNew' ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : null
                }
              />
              <PlaceOrderButton
                title={
                  deliveryValidation.isChecking
                    ? "Validating delivery location..."
                    : isPaymentLoading
                      ? "Calculating the final price"
                      : "Place order & View Details"
                }
                // require Done click (isDoneClicked) when items exist
                isDisabled={isLoading || orderItems.length === 0 || !isDoneClicked || isPaymentLoading || deliveryValidation.isChecking ||
                  !deliveryValidation.isValid || isUserCreationLoading ||
                  (enableManualDeliveryFeeWatcher && manualDeliveryFeeError !== "") ||
                  (enableManualDiscountWatcher && manualDiscountError !== "") ||
                  unavailableItemIds.size > 0 ||
                  clickedButton === 'addNew'}
                onClick={
                  isNewCustomer ? () => handleUserCreation(true) : handleSubmit((data) => onSubmit(data, { viewAfter: true }))
                }
                icon={
                  (isLoading || isLoadingCustomerCreate || isPaymentLoading) && clickedButton === 'viewDetails' ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : null
                }
              />
            </div>
          </div>
        </Col>
      </Row>
      <br />

    </>
  );
};

export default CreateOrder;